import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import dateformat from "dateformat";
import moment from "moment";

import Text from "src/components/text";

import { StatCard, StatCardGrid } from "../../common";
import { TableCellText } from "../../common/TableCellText";
import { LeadDetails, CustomFieldsDetails } from "./components/LeadDetails";
import { LeadJourney } from "./components/LeadJourney";
import { PageInteractions } from "./components/PageInteractions";
import { AssetInteractions } from "./components/AssetInteractions";
import {
  useLatestCampaignLeadQuery,
  useLeadDetailQuery,
} from "src/query/experiences/leadsAndReports/leads";
import { useLeadRecordQuery } from "src/query/experiences/leadsAndReports/reports";
import {
  selectDateFilter,
  selectSelectedLead,
} from "src/redux/selectors/experiences/leadsAndReports";
import { clearSelectedLead } from "src/redux/actions/experiences/leadsAndReports";
import {
  convertDatesToTimezone,
  dateKey,
} from "src/services/experiences/leadsAndReports/dateUtils";
import { renderInitials, formatDurationWithLabel } from "../../utils/format";
import styles from "./leads.module.scss";

interface LeadProfileProps {
  storyboardId: string;
  storyboardName?: string;
  storyboardType?: string;
}

export const LeadProfile: React.FC<LeadProfileProps> = ({
  storyboardId,
  storyboardName,
  storyboardType,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selected = useSelector(selectSelectedLead);
  const dateFilter = useSelector(selectDateFilter);

  const handleBack = () => {
    dispatch(clearSelectedLead());
    navigate(location.pathname.replace(/\/profile.*$/, ""), { replace: true });
  };

  const profileId = selected?.profileId ?? "";
  const data = selected?.data ?? {};
  const customFields = data.fields || [];
  const key = dateKey(dateFilter ?? undefined);

  const request = {
    storyboard: storyboardId,
    metricType: "leads",
    ...convertDatesToTimezone(dateFilter ?? undefined),
  };

  const { data: leadProfile = {}, isLoading } = useLeadDetailQuery(
    profileId,
    request,
    key,
  );
  const { data: latestCampaign = {} } = useLatestCampaignLeadQuery(
    profileId,
    request,
    key,
  );

  const firstDefined = <T,>(...candidates: T[]): T | null =>
    candidates.find((c) => c !== undefined && c !== null && c !== "") ?? null;

  const needsRecordLookup =
    customFields.length === 0 ||
    firstDefined(_.get(data, "campaignName"), _.get(data, "contact.campaignName")) ===
      null;
  const { data: leadRecord } = useLeadRecordQuery(
    profileId,
    request,
    key,
    needsRecordLookup,
  );

  const fromRecord = (uid: string) =>
    firstDefined(
      _.get(data, uid),
      _.get(data, `contact.${uid}`),
      _.get(leadRecord, uid),
      _.get(leadRecord, `contact.${uid}`),
    );

  const capturedOn = firstDefined(
    _.get(data, "contact.createdDate"),
    _.get(leadRecord, "contact.createdDate"),
    _.get(leadProfile, "contact.createdDate"),
    _.get(leadProfile, "createdDate"),
  );
  const campaignName = firstDefined(
    fromRecord("campaignName"),
    _.get(latestCampaign, "campaignName"),
    _.get(leadProfile, "campaignName"),
  );
  const campaignSource = firstDefined(
    fromRecord("campaignSource"),
    _.get(latestCampaign, "campaignSource"),
    _.get(leadProfile, "campaignSource"),
  );
  const campaignMedium = firstDefined(
    fromRecord("campaignMedium"),
    _.get(latestCampaign, "campaignMedium"),
    _.get(leadProfile, "campaignMedium"),
  );

  const fields =
    [customFields, _.get(leadRecord, "fields")].find(
      (f) => Array.isArray(f) && f.length > 0,
    ) ?? [];

  const lastActivity = leadProfile.lastEventDate
    ? moment(leadProfile.lastEventDate).fromNow()
    : "-";

  return (
    <div className={styles.leadProfile}>
      <section className={styles.detailsContainer}>
        <div className={styles.profileOverview}>
          <div className={styles.basicInfo}>
            <div className={styles.avatar}>{renderInitials(leadProfile)}</div>
            <div className={styles.basicInfoText}>
              <Text
                className={styles.fullName}
                text={`${leadProfile.firstName || ""} ${leadProfile.lastName || ""}`}
              />
              <Text
                className={styles.email}
                text={_.get(leadProfile, "email", "-")}
              />
              <TableCellText
                className={styles.phone}
                text={_.get(leadProfile, "workPhone") || "-"}
              />
            </div>
          </div>
        </div>

        <div className={styles.customFieldsContainer}>
          <div className={styles.customFieldPill}>
            <p className={styles.customFieldLabel}>Captured On:</p>
            <TableCellText
              className={styles.customFieldValue}
              text={
                capturedOn
                  ? dateformat(
                      capturedOn as string | number | Date,
                      "dd mmm yyyy",
                    )
                  : "-"
              }
            />
          </div>
          <div className={styles.customFieldPill}>
            <p className={styles.customFieldLabel}>Campaign/Page:</p>
            <TableCellText
              className={styles.customFieldValue}
              text={(campaignName || "-") + " / " + (storyboardName || "-")}
            />
          </div>
          <div className={styles.customFieldPill}>
            <p className={styles.customFieldLabel}>Source/Medium:</p>
            <TableCellText
              className={styles.customFieldValue}
              text={(campaignSource || "-") + " / " + (campaignMedium || "-")}
            />
          </div>
          <div className={styles.customFieldPill}>
            <p className={styles.customFieldLabel}>Last Activity:</p>
            <TableCellText
              className={styles.customFieldValue}
              text={lastActivity}
            />
          </div>
        </div>

        <LeadDetails leadProfile={leadProfile} />
        <CustomFieldsDetails data={fields} />
        <LeadJourney
          storyboardId={storyboardId}
          profileId={profileId}
          dateFilter={dateFilter}
        />
      </section>

      <div className={styles.interactions}>
        <StatCardGrid>
          <StatCard
            heading="Views Recorded"
            value={Math.round(_.get(leadProfile, "views", 0))}
            icon="/images/experiences/reports/sessions.svg"
            loading={isLoading}
          />
          <StatCard
            heading="Total Time Spent"
            value={
              formatDurationWithLabel(_.get(leadProfile, "timeSpent", 0)).value
            }
            icon="/images/experiences/leads/timeSpent.svg"
            loading={isLoading}
          />
          <StatCard
            heading="Completion Percentage"
            value={`${_.get(leadProfile, "completion", 0)}%`}
            icon="/images/experiences/leads/completion.svg"
            loading={isLoading}
          />
        </StatCardGrid>
        <PageInteractions
          storyboardId={storyboardId}
          profileId={profileId}
          dateFilter={dateFilter}
          storyboardType={storyboardType}
        />
        <AssetInteractions
          storyboardId={storyboardId}
          profileId={profileId}
          dateFilter={dateFilter}
        />
      </div>
    </div>
  );
};
